<template>
  <div class="dashboard-page pb-4">
    <div class="dashboard-heading d-flex flex-wrap align-items-center mb-4">
      <div>
        <h4 class="mb-1">Selamat datang, {{ accountName }}</h4>
        <p class="text-muted mb-0">Pantau aktivitas toko dan sisa stok dari satu halaman.</p>
      </div>
      <div class="dashboard-filter ml-auto mt-3 mt-md-0">
        <b-form inline @submit.prevent="loadDashboard">
          <b-form-select v-model="filters.period" :options="periodOptions" class="mr-2" />
          <b-form-input v-model="filters.date" type="date" class="mr-2" />
          <b-button type="submit" variant="primary" :disabled="isLoading">
            <font-awesome-icon v-if="isLoading" icon="spinner" spin />
            <span v-else>Tampilkan</span>
          </b-button>
        </b-form>
      </div>
    </div>

    <b-alert v-if="errorMessage" show variant="danger" class="mb-4">
      {{ errorMessage }}
      <b-button size="sm" variant="outline-danger" class="ml-2" @click="loadDashboard">Coba Lagi</b-button>
    </b-alert>

    <b-row class="mb-4">
      <b-col cols="12" sm="6" xl="3" class="mb-3 mb-xl-0">
        <div class="dashboard-stat dashboard-stat-sales">
          <div class="dashboard-stat-icon"><font-awesome-icon icon="cash-register" /></div>
          <div>
            <div class="dashboard-stat-label">Penjualan Disetujui</div>
            <div class="dashboard-stat-value">Rp{{ $formatPrice(summary.sales.total) }}</div>
            <div class="dashboard-stat-note">{{ summary.sales.total_transactions }} transaksi</div>
          </div>
        </div>
      </b-col>
      <b-col cols="12" sm="6" xl="3" class="mb-3 mb-xl-0">
        <div class="dashboard-stat dashboard-stat-buyback">
          <div class="dashboard-stat-icon"><font-awesome-icon icon="shopping-cart" /></div>
          <div>
            <div class="dashboard-stat-label">Buy Back Disetujui</div>
            <div class="dashboard-stat-value">Rp{{ $formatPrice(summary.buy_backs.total) }}</div>
            <div class="dashboard-stat-note">{{ summary.buy_backs.total_transactions }} transaksi</div>
          </div>
        </div>
      </b-col>
      <b-col cols="12" sm="6" xl="3" class="mb-3 mb-sm-0">
        <div class="dashboard-stat dashboard-stat-craft">
          <div class="dashboard-stat-icon"><font-awesome-icon icon="star" /></div>
          <div>
            <div class="dashboard-stat-label">Tempahan Disetujui</div>
            <div class="dashboard-stat-value">{{ summary.crafts.total_transactions }}</div>
            <div class="dashboard-stat-note">Rp{{ $formatPrice(summary.crafts.total) }}</div>
          </div>
        </div>
      </b-col>
      <b-col v-if="isAdmin" cols="12" sm="6" xl="3">
        <router-link to="/reviews" class="dashboard-stat dashboard-stat-review">
          <div class="dashboard-stat-icon"><font-awesome-icon icon="clipboard" /></div>
          <div>
            <div class="dashboard-stat-label">Menunggu Review</div>
            <div class="dashboard-stat-value">{{ reviewPending }}</div>
            <div class="dashboard-stat-note">Buka antrean persetujuan</div>
          </div>
        </router-link>
      </b-col>
      <b-col v-else cols="12" sm="6" xl="3">
        <div class="dashboard-stat dashboard-stat-transactions">
          <div class="dashboard-stat-icon"><font-awesome-icon icon="wallet" /></div>
          <div>
            <div class="dashboard-stat-label">Nominal Transaksi</div>
            <div class="dashboard-stat-value">Rp{{ $formatPrice(transactionTotal) }}</div>
            <div class="dashboard-stat-note">Penjualan + Buy Back + Tempahan</div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row class="mb-4">
      <b-col cols="12" lg="8" class="mb-4 mb-lg-0">
        <b-card no-header class="h-100 dashboard-card">
          <div class="d-flex align-items-center mb-3">
            <div>
              <h5 class="mb-1">Ringkasan Aktivitas</h5>
              <small class="text-muted">{{ periodDescription }}</small>
            </div>
            <router-link to="/reports" class="ml-auto small">Lihat laporan</router-link>
          </div>
          <div class="activity-bars">
            <div v-for="item in activitySummary" :key="item.label" class="activity-bar-row">
              <div class="d-flex justify-content-between mb-1">
                <span>{{ item.label }}</span>
                <strong>{{ item.count }} transaksi</strong>
              </div>
              <div class="activity-bar-track">
                <div class="activity-bar-fill" :class="item.className" :style="{ width: activityWidth(item.count) }"></div>
              </div>
              <small class="text-muted">{{ item.amount }}</small>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col cols="12" lg="4">
        <b-card no-header class="h-100 dashboard-card">
          <div class="d-flex align-items-center mb-3">
            <h5 class="mb-0">Aksi Cepat</h5>
          </div>
          <div class="quick-actions">
            <router-link to="/sales/create" class="quick-action">
              <span class="quick-action-icon bg-primary"><font-awesome-icon icon="cash-register" /></span>
              <span>Tambah Penjualan</span>
            </router-link>
            <router-link to="/stock-ins/create" class="quick-action">
              <span class="quick-action-icon bg-success"><font-awesome-icon icon="truck" /></span>
              <span>Tambah Stok Masuk</span>
            </router-link>
            <router-link to="/crafts/create" class="quick-action">
              <span class="quick-action-icon bg-warning"><font-awesome-icon icon="star" /></span>
              <span>Tambah Tempahan</span>
            </router-link>
            <router-link to="/scan-invoice" class="quick-action">
              <span class="quick-action-icon bg-info"><font-awesome-icon icon="barcode" /></span>
              <span>Scan Invoice</span>
            </router-link>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="mb-4">
      <b-col cols="12">
        <b-card no-header class="dashboard-card">
          <div class="d-flex flex-wrap align-items-center mb-3">
            <div>
              <h5 class="mb-1">Sisa Stok Berdasarkan Kadar</h5>
              <small class="text-muted">Dikelompokkan dari nilai kadar yang tersimpan pada stok aktif.</small>
            </div>
            <router-link to="/stocks" class="ml-auto small mt-2 mt-sm-0">Kelola stok</router-link>
          </div>
          <small class="d-block text-muted mb-2">Klik baris kadar untuk melihat rincian stoknya.</small>
          <div class="table-responsive">
            <table class="table table-hover dashboard-table mb-0">
              <thead>
                <tr>
                  <th>Jenis Barang</th>
                  <th>Kadar</th>
                  <th class="text-right">Sisa Stok</th>
                  <th>Satuan</th>
                  <th class="text-right">Jumlah Produk</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in summary.stocks"
                  :key="stockKey(row)"
                  class="dashboard-stock-row"
                  tabindex="0"
                  title="Klik untuk melihat rincian stok"
                  @click="openStockDetails(row)"
                  @keyup.enter="openStockDetails(row)"
                >
                  <td>{{ stockTypeLabel(row.jenis_barang) }}</td>
                  <td>{{ row.kadar === null ? 'Tanpa kadar' : row.kadar }}</td>
                  <td class="text-right font-weight-bold">{{ $formatPrice(row.total) }}</td>
                  <td>{{ row.satuan }}</td>
                  <td class="text-right">{{ $formatPrice(row.jumlah_item) }}</td>
                </tr>
                <tr v-if="!summary.stocks.length">
                  <td colspan="5" class="text-center text-muted py-4">Belum ada sisa stok aktif.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12" lg="4" class="mb-4 mb-lg-0">
        <b-card no-header class="dashboard-card">
          <div class="d-flex align-items-center mb-3">
            <h5 class="mb-0">Penjualan Terbaru</h5>
            <router-link to="/sales" class="ml-auto small">Lihat semua</router-link>
          </div>
          <div v-if="summary.sales.data.length" class="dashboard-list">
            <router-link v-for="row in summary.sales.data" :key="row.id" :to="'/sales/' + row.id" class="dashboard-list-item">
              <div>
                <strong>{{ row.transaction_no }}</strong>
                <small class="d-block text-muted">{{ row.costumer ? row.costumer.nama : 'Tanpa pelanggan' }} · {{ formatDate(row.tanggal_transaksi) }}</small>
              </div>
              <span class="font-weight-bold">Rp{{ $formatPrice(row.total) }}</span>
            </router-link>
          </div>
          <div v-else class="empty-dashboard">Belum ada penjualan pada periode ini.</div>
        </b-card>
      </b-col>
      <b-col cols="12" lg="4" class="mb-4 mb-lg-0">
        <b-card no-header class="dashboard-card">
          <div class="d-flex align-items-center mb-3">
            <h5 class="mb-0">Buy Back Terbaru</h5>
            <router-link to="/buy-backs" class="ml-auto small">Lihat semua</router-link>
          </div>
          <div v-if="summary.buy_backs.data.length" class="dashboard-list">
            <router-link v-for="row in summary.buy_backs.data" :key="row.id" :to="'/buy-backs/' + row.id" class="dashboard-list-item">
              <div>
                <strong>{{ row.transaction_no }}</strong>
                <small class="d-block text-muted">{{ row.costumer ? row.costumer.nama : 'Tanpa pelanggan' }} · {{ formatDate(row.tanggal_transaksi) }}</small>
              </div>
              <span class="font-weight-bold">Rp{{ $formatPrice(row.total) }}</span>
            </router-link>
          </div>
          <div v-else class="empty-dashboard">Belum ada buy back pada periode ini.</div>
        </b-card>
      </b-col>
      <b-col cols="12" lg="4">
        <b-card no-header class="dashboard-card">
          <div class="d-flex align-items-center mb-3">
            <h5 class="mb-0">Tempahan Terbaru</h5>
            <router-link to="/crafts" class="ml-auto small">Lihat semua</router-link>
          </div>
          <div v-if="summary.crafts.data.length" class="dashboard-list">
            <router-link v-for="row in summary.crafts.data" :key="row.id" :to="'/crafts/' + row.id" class="dashboard-list-item">
              <div>
                <strong>{{ row.transaction_no }}</strong>
                <small class="d-block text-muted">{{ row.costumer ? row.costumer.nama : 'Tanpa pelanggan' }} · {{ formatDate(row.tanggal_transaksi) }}</small>
              </div>
              <span class="font-weight-bold">Rp{{ $formatPrice(row.total) }}</span>
            </router-link>
          </div>
          <div v-else class="empty-dashboard">Belum ada tempahan pada periode ini.</div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Report from '@/models/Report.js'
import { Roles } from '@/_helpers'

const reportModel = new Report()

export default {
  name: 'Dashboard',
  data () {
    return {
      isLoading: false,
      errorMessage: null,
      filters: {
        period: 'monthly',
        date: this.$moment().format('YYYY-MM-DD')
      },
      periodOptions: [
        { value: 'daily', text: 'Harian' },
        { value: 'weekly', text: 'Mingguan' },
        { value: 'monthly', text: 'Bulanan' },
        { value: 'yearly', text: 'Tahunan' }
      ],
      summary: {
        start: null,
        end: null,
        sales: { total_transactions: 0, total: 0, data: [] },
        buy_backs: { total_transactions: 0, total: 0, data: [] },
        crafts: { total_transactions: 0, total: 0, data: [] },
        stocks: [],
        review: { pending: 0 }
      }
    }
  },
  computed: {
    isAdmin () {
      return this.$store.getters['account/accountData'].role === Roles.ADMIN
    },
    accountName () {
      return this.$store.getters['account/accountData'].name || 'Pengguna'
    },
    reviewPending () {
      return this.summary.review ? this.summary.review.pending : 0
    },
    transactionTotal () {
      return this.summary.sales.total + this.summary.buy_backs.total + this.summary.crafts.total
    },
    periodDescription () {
      if (!this.summary.start) return 'Belum ada data periode'
      const start = this.formatDate(this.summary.start)
      const end = this.summary.end && this.summary.end !== this.summary.start ? this.formatDate(this.summary.end) : null
      return end ? start + ' sampai ' + end : start
    },
    activitySummary () {
      return [
        { label: 'Penjualan', count: this.summary.sales.total_transactions, amount: 'Rp' + this.$formatPrice(this.summary.sales.total), className: 'fill-primary' },
        { label: 'Buy Back', count: this.summary.buy_backs.total_transactions, amount: 'Rp' + this.$formatPrice(this.summary.buy_backs.total), className: 'fill-success' },
        { label: 'Tempahan', count: this.summary.crafts.total_transactions, amount: 'Rp' + this.$formatPrice(this.summary.crafts.total), className: 'fill-warning' }
      ]
    },
    maxActivityCount () {
      return Math.max.apply(null, this.activitySummary.map(item => item.count).concat([1]))
    }
  },
  mounted () {
    this.loadDashboard()
  },
  methods: {
    loadDashboard () {
      this.isLoading = true
      this.errorMessage = null
      reportModel.dashboard(this.filters.period, this.filters.date).then(response => {
        this.summary = Object.assign(this.summary, response)
      }).catch(() => {
        this.errorMessage = 'Data dashboard gagal dimuat. Silakan coba lagi.'
      }).finally(() => {
        this.isLoading = false
      })
    },
    formatDate (value) {
      return value ? this.$moment(value).format('DD-MM-YYYY') : '-'
    },
    activityWidth (count) {
      return Math.max((count / this.maxActivityCount) * 100, count ? 5 : 0) + '%'
    },
    stockKey (row) {
      return row.jenis_barang + '-' + (row.kadar === null ? 'tanpa-kadar' : row.kadar)
    },
    openStockDetails (row) {
      const query = {
        jenis_barang: row.jenis_barang,
        approval_status: 'approved',
        available_only: 1
      }
      if (row.kadar !== null && row.kadar !== '') {
        query.kadar = row.kadar
      } else if (row.jenis_barang !== 'berlian') {
        query.kadar_kosong = 1
      }
      this.$router.push({ path: '/stocks', query }).catch(() => {})
    },
    stockTypeLabel (type) {
      return { emas: 'Emas', berlian: 'Berlian', perhiasan: 'Perhiasan' }[type] || type
    }
  }
}
</script>

<style scoped>
.dashboard-page { padding-top: 0.25rem; }
.dashboard-heading h4 { color: #1f2937; font-weight: 700; }
.dashboard-filter select, .dashboard-filter input { min-width: 140px; }
.dashboard-stat {
  min-height: 128px;
  padding: 1.15rem;
  border-radius: 0.5rem;
  color: #fff;
  display: flex;
  align-items: center;
  box-shadow: 0 8px 20px rgba(31, 41, 55, 0.12);
}
.dashboard-stat:hover { color: #fff; text-decoration: none; transform: translateY(-1px); }
.dashboard-stat-sales { background: linear-gradient(135deg, #2563eb, #1d4ed8); }
.dashboard-stat-buyback { background: linear-gradient(135deg, #059669, #047857); }
.dashboard-stat-craft { background: linear-gradient(135deg, #d97706, #b45309); }
.dashboard-stat-review { background: linear-gradient(135deg, #7c3aed, #6d28d9); }
.dashboard-stat-transactions { background: linear-gradient(135deg, #0891b2, #0e7490); }
.dashboard-stat-icon {
  width: 46px;
  height: 46px;
  margin-right: 0.9rem;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.18);
  font-size: 1.25rem;
  flex-shrink: 0;
}
.dashboard-stat-label { font-size: 0.8rem; opacity: 0.9; }
.dashboard-stat-value { margin-top: 0.15rem; font-size: 1.3rem; line-height: 1.25; font-weight: 700; }
.dashboard-stat-note { margin-top: 0.2rem; font-size: 0.75rem; opacity: 0.85; }
.dashboard-card { border-radius: 0.5rem; }
.dashboard-card h5 { color: #1f2937; font-weight: 700; }
.activity-bar-row + .activity-bar-row { margin-top: 1.25rem; }
.activity-bar-track { height: 9px; overflow: hidden; border-radius: 20px; background: #edf0f5; }
.activity-bar-fill { height: 100%; border-radius: inherit; transition: width 0.3s ease; }
.fill-primary { background: #2563eb; }
.fill-success { background: #059669; }
.fill-warning { background: #d97706; }
.quick-action { color: #374151; display: flex; align-items: center; padding: 0.55rem 0; border-bottom: 1px solid #eef0f3; }
.quick-action:last-child { border-bottom: 0; }
.quick-action:hover { color: #2563eb; text-decoration: none; }
.quick-action-icon { width: 34px; height: 34px; margin-right: 0.75rem; border-radius: 0.4rem; color: #fff; display: inline-flex; align-items: center; justify-content: center; }
.dashboard-table th { border-top: 0; color: #6b7280; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.02em; }
.dashboard-table td { vertical-align: middle; }
.dashboard-stock-row { cursor: pointer; }
.dashboard-stock-row:focus { outline: 2px solid #2563eb; outline-offset: -2px; }
.dashboard-list-item { display: flex; align-items: center; justify-content: space-between; color: #374151; padding: 0.75rem 0; border-bottom: 1px solid #eef0f3; }
.dashboard-list-item:last-child { border-bottom: 0; }
.dashboard-list-item:hover { color: #2563eb; text-decoration: none; }
.dashboard-list-item small { font-size: 0.75rem; }
.empty-dashboard { color: #6b7280; padding: 1.5rem 0; text-align: center; }
@media (max-width: 575px) {
  .dashboard-filter, .dashboard-filter form, .dashboard-filter select, .dashboard-filter input, .dashboard-filter button { width: 100%; }
  .dashboard-filter select, .dashboard-filter input, .dashboard-filter button { margin-right: 0 !important; margin-bottom: 0.5rem; }
}
</style>
